/**
 * useBusyRetryPending - true while a read on screen is waiting on its
 * busy retry (build-specs/busy-says-so-and-retries-once.md R4).
 *
 * Read from the query cache, so no screen changes to show the notice. A
 * query counts when it has an observer on screen, has no data yet, is
 * still fetching (or paused, because the tab is hidden) and its latest
 * failure was a busy database. A background refetch of data already on
 * screen does not count: the person still sees the data.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { notifyManager, useQueryClient, type Query } from '@tanstack/react-query';
import { isDatabaseBusy } from '@/lib/api';

function waitsOnBusyRetry(query: Query): boolean {
  const { data, fetchStatus, fetchFailureReason } = query.state;
  return (
    query.getObserversCount() > 0 &&
    data === undefined &&
    fetchStatus !== 'idle' &&
    isDatabaseBusy(fetchFailureReason)
  );
}

export function useBusyRetryPending(): boolean {
  const queryCache = useQueryClient().getQueryCache();
  const subscribe = useCallback(
    (onStoreChange: () => void) => queryCache.subscribe(notifyManager.batchCalls(onStoreChange)),
    [queryCache],
  );
  const snapshot = (): boolean => queryCache.getAll().some(waitsOnBusyRetry);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
